/**
 * Adaptive quality-gate pacing module.
 *
 * Provides pure functions to compute the next dispatch interval and smart-skip
 * predicate for the quality gate rAF loop. Both enroll and auth loops share these
 * constants so device performance is weighted consistently (Q5).
 *
 * Spec scenarios: AQG-1, AQG-2, AQG-3
 */

/** Minimum quality gate interval — never fire faster than 250ms even on fast devices. */
export const QUALITY_FRAME_INTERVAL_MIN_MS = 250;

/** Multiplier on lastInferenceMs to derive the next gate interval. */
export const PACING_FACTOR = 1.2;

/**
 * If lastInferenceMs exceeds this, the next rAF tick should skip dispatching analysis
 * to avoid overlapping analysis dispatches on slow devices.
 */
export const SMART_SKIP_THRESHOLD_MS = 200;

/**
 * Adaptive quality gate interval. Pure function.
 *
 * Returns max(QUALITY_FRAME_INTERVAL_MIN_MS, lastInferenceMs * PACING_FACTOR).
 *
 * AQG-1: fast device (lastMs ≤ 200ms) → result is clamped to 250ms.
 * AQG-2: slow device (lastMs = 400ms) → result is 480ms (400 × 1.2).
 *
 * @param lastInferenceMs - Duration of the last analyzeQuality call in milliseconds.
 * @returns Next gate interval in milliseconds (always ≥ QUALITY_FRAME_INTERVAL_MIN_MS).
 */
export function nextInterval(lastInferenceMs: number): number {
  return Math.max(QUALITY_FRAME_INTERVAL_MIN_MS, lastInferenceMs * PACING_FACTOR);
}

/**
 * Smart skip predicate. Pure function. Currently UNUSED in production.
 *
 * The original intent was to defer one rAF tick after a slow inference to avoid
 * stacking analyses. The naive integration in useScanLifecycle / AdminEnrollFacePage
 * locked the loop on slow devices: lastInferenceMs stays > 200ms across iterations
 * because we never get to update it while skipping, so every tick returns true
 * forever and the loop never resumes analysing. nextInterval() already provides
 * correct throttling on slow devices, so the integration removed this call.
 *
 * Kept exported as a pure function for reference / future re-introduction with a
 * proper stateful "skip exactly N ticks once" implementation.
 *
 * AQG-3: lastMs = 350ms (> 200ms) → returns true.
 *
 * @param lastInferenceMs - Duration of the last analyzeQuality call in milliseconds.
 * @returns true when the caller would, in theory, want to defer the next dispatch.
 */
export function shouldSkip(lastInferenceMs: number): boolean {
  return lastInferenceMs > SMART_SKIP_THRESHOLD_MS;
}
